// src/pages/Tarifs/components/PricingCard.jsx

import React from "react";
import { FaArrowRight, FaCheck } from "react-icons/fa";

import { cn } from "../utils/pricingUtils";

function FeatureList({ features = [] }) {
  return (
    <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
      {features.map((feature) => (
        <div
          key={feature}
          className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
        >
          <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
            <FaCheck className="text-[8px]" />
          </span>

          <span>{feature}</span>
        </div>
      ))}
    </div>
  );
}

function SinglePrice({ application }) {
  return (
    <div className="rounded-[20px] border border-[#0868f2]/20 bg-blue-50/60 p-6 dark:border-blue-400/20 dark:bg-blue-500/10">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0868f2] dark:text-blue-300">
        Tarif annuel
      </p>

      <div className="mt-4 flex items-end gap-2">
        <span className="text-5xl font-semibold tracking-[-0.045em] text-slate-950 dark:text-white">
          {application.priceText}
        </span>

        {application.suffix ? (
          <span className="pb-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
            {application.suffix}
          </span>
        ) : null}
      </div>

      {application.note ? (
        <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">
          {application.note}
        </p>
      ) : null}
    </div>
  );
}

function MultiplePlans({ plans = [] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {plans.map((plan) => (
        <article
          key={plan.name}
          className={cn(
            "relative rounded-[20px] border p-6",
            plan.highlight
              ? [
                  "border-[#0868f2] bg-blue-50/70",
                  "dark:border-blue-400 dark:bg-blue-500/10",
                ]
              : [
                  "border-slate-200 bg-white",
                  "dark:border-white/10 dark:bg-slate-950",
                ],
          )}
        >
          {plan.highlight ? (
            <span className="absolute right-4 top-4 rounded-full bg-[#ff7900] px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-white">
              Recommandé
            </span>
          ) : null}

          <p className="pr-20 text-sm font-semibold text-slate-950 dark:text-white">
            {plan.name}
          </p>

          <div className="mt-5 flex items-end gap-2">
            <span className="text-4xl font-semibold tracking-[-0.04em] text-slate-950 dark:text-white">
              {plan.priceText}
            </span>

            <span className="pb-1 text-xs font-medium text-slate-500 dark:text-slate-400">
              {plan.suffix}
            </span>
          </div>

          {plan.description ? (
            <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
              {plan.description}
            </p>
          ) : null}
        </article>
      ))}
    </div>
  );
}

export default function PricingCard({ application }) {
  if (!application) return null;

  const Icon = application.icon;

  return (
    <section
      id="selected-pricing"
      className="scroll-mt-28 border-t border-slate-200 pt-12 dark:border-white/10"
    >
      <div className="grid gap-10 xl:grid-cols-[0.82fr_1.18fr] xl:items-start">
        <div>
          <div className="flex items-center gap-4">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-blue-50 text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
              {Icon ? <Icon /> : null}
            </span>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ff7900]">
                Votre sélection
              </p>

              <h2 className="mt-1 text-3xl font-semibold tracking-[-0.035em] text-slate-950 dark:text-white">
                {application.name}
              </h2>
            </div>
          </div>

          <p className="mt-6 text-base leading-8 text-slate-600 dark:text-slate-300">
            {application.short}
          </p>

          <p className="mt-4 border-l-2 border-[#ff7900] pl-4 text-sm leading-7 text-slate-500 dark:text-slate-400">
            {application.audience}
          </p>

          <a
            href="#contact"
            className="group mt-7 inline-flex h-12 items-center justify-center gap-3 rounded-full bg-[#0868f2] px-6 text-sm font-semibold text-white transition hover:bg-[#0648b8]"
          >
            Demander une présentation

            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900]">
              <FaArrowRight className="text-[9px] transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>
        </div>

        <div>
          {application.plans?.length ? (
            <MultiplePlans plans={application.plans} />
          ) : (
            <SinglePrice application={application} />
          )}

          <div className="mt-8 border-t border-slate-200 pt-7 dark:border-white/10">
            <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              Ce qui est inclus
            </p>

            <FeatureList features={application.features} />
          </div>
        </div>
      </div>
    </section>
  );
}